import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createSlug } from '../../util/urlHelpers';
import { pathByRouteName } from '../../util/routes';
import { formatCartPrice, formatCartSubtotal } from '../../util/cartDisplay';
import { formatVariantSummary } from '../../util/productVariants';
import { CUSTOMER_USER_TYPE } from '../../config/userRoles';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import {
  hydrateCart,
  updateCartQuantity,
  removeFromCart,
  restoreCartItem,
  clearCart,
  lineIdOf,
  selectCartItems,
  selectCartCount,
  selectIsCartHydrated,
} from '../../ducks/cart.duck';

import {
  Page,
  LayoutSingleColumn,
  Heading,
  NamedLink,
  IconCart,
  InlineTextButton,
  OrderEcolabelSummary,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './CartPage.module.css';

// How many placeholder lines to show while the stored basket is being read.
const SKELETON_LINES = 2;

/**
 * A single basket line: image, title, author, price, quantity stepper, remove.
 */
const CartLineItem = props => {
  const { item, intl, onUpdateQuantity, onRemove } = props;
  const {
    listingId,
    title,
    price,
    authorName,
    imageURL,
    quantity,
    productVariants,
    maxQuantity,
  } = item;
  const lineId = lineIdOf(item);
  // A brand holding two Small Reds can't sell a third, so the stepper stops.
  const isAtMaxQuantity = Number.isInteger(maxQuantity) && quantity >= maxQuantity;
  const slug = createSlug(title || 'listing');
  const linePrice = formatCartPrice(intl, price, quantity);
  const unitPrice = formatCartPrice(intl, price, 1);
  // Which version of the product this line is for, e.g. "Size: Medium · Volume: 100ml".
  const variantSummary = formatVariantSummary(productVariants);

  return (
    <li className={css.lineItem}>
      <NamedLink className={css.lineImageLink} name="ListingPage" params={{ id: listingId, slug }}>
        {imageURL ? (
          <img className={css.lineImage} src={imageURL} alt={title} />
        ) : (
          <div className={css.lineImagePlaceholder}>
            <IconCart className={css.lineImagePlaceholderIcon} />
          </div>
        )}
      </NamedLink>

      <div className={css.lineInfo}>
        {authorName ? <div className={css.lineAuthor}>{authorName}</div> : null}
        <NamedLink className={css.lineTitle} name="ListingPage" params={{ id: listingId, slug }}>
          {title}
        </NamedLink>
        {variantSummary ? <div className={css.lineVariants}>{variantSummary}</div> : null}
        {unitPrice ? <div className={css.lineUnitPrice}>{unitPrice}</div> : null}
        {isAtMaxQuantity ? (
          <div className={css.lineStockNote}>
            <FormattedMessage id="CartPage.maxQuantityReached" values={{ maxQuantity }} />
          </div>
        ) : null}

        <div className={css.lineControls}>
          <div className={css.quantityStepper}>
            <button
              type="button"
              className={css.quantityButton}
              aria-label={
                quantity <= 1
                  ? intl.formatMessage({ id: 'CartPage.removeItem' }, { title })
                  : intl.formatMessage({ id: 'CartPage.decreaseQuantity' })
              }
              // At 1, "−" removes the line — with the same undo as "Remove".
              onClick={() =>
                quantity <= 1 ? onRemove(item) : onUpdateQuantity(lineId, quantity - 1)
              }
            >
              −
            </button>
            <span className={css.quantityValue} aria-live="polite">
              {quantity}
            </span>
            <button
              type="button"
              className={css.quantityButton}
              aria-label={intl.formatMessage({ id: 'CartPage.increaseQuantity' })}
              disabled={isAtMaxQuantity}
              title={
                isAtMaxQuantity
                  ? intl.formatMessage({ id: 'CartPage.maxQuantityReached' }, { maxQuantity })
                  : undefined
              }
              onClick={() => onUpdateQuantity(lineId, quantity + 1)}
            >
              +
            </button>
          </div>
          <InlineTextButton rootClassName={css.removeButton} onClick={() => onRemove(item)}>
            <FormattedMessage id="CartPage.remove" />
          </InlineTextButton>
        </div>
      </div>

      <div className={css.linePrice}>{linePrice}</div>
    </li>
  );
};

/**
 * "Removed X. Undo" — shown after a line is taken out of the basket, so a
 * mis-tap on "−" or "Remove" costs one tap to put right.
 */
const UndoRemoval = props => {
  const { removed, onUndo, onDismiss } = props;
  if (!removed) {
    return null;
  }
  return (
    <div className={css.undoBar} role="status">
      <span className={css.undoText}>
        <FormattedMessage id="CartPage.itemRemoved" values={{ title: removed.item.title }} />
      </span>
      <button type="button" className={css.undoButton} onClick={onUndo}>
        <FormattedMessage id="CartPage.undo" />
      </button>
      <button type="button" className={css.undoDismiss} onClick={onDismiss}>
        <FormattedMessage id="CartPage.dismiss" />
      </button>
    </div>
  );
};

/**
 * Checkout needs an account (orders, messages with the brand and refunds all
 * hang off it), so a signed-out shopper picks between logging in and signing
 * up here, rather than being dropped on a sign-up form they may not need.
 */
const CheckoutActions = props => {
  const { isAuthenticated, checkoutPath, className } = props;
  if (isAuthenticated) {
    return (
      <div className={className}>
        <NamedLink name="CartCheckoutPage" className={css.checkoutButton}>
          <FormattedMessage id="CartPage.checkoutButton" />
        </NamedLink>
      </div>
    );
  }
  const authLinkProps = {
    params: { userType: CUSTOMER_USER_TYPE },
    to: { state: { from: checkoutPath } },
  };
  return (
    <div className={className}>
      <NamedLink name="LoginForUserTypePage" {...authLinkProps} className={css.checkoutButton}>
        <FormattedMessage id="CartPage.loginToCheckout" />
      </NamedLink>
      <NamedLink name="SignupForUserTypePage" {...authLinkProps} className={css.signupButton}>
        <FormattedMessage id="CartPage.signupToCheckout" />
      </NamedLink>
    </div>
  );
};

const CartSkeleton = () => (
  <div className={css.layout} aria-busy="true">
    <div className={css.main}>
      <div className={css.skeletonHeading} />
      <ul className={css.lineItems}>
        {[...Array(SKELETON_LINES).keys()].map(i => (
          <li key={i} className={css.lineItem}>
            <div className={css.skeletonImage} />
            <div className={css.lineInfo}>
              <div className={css.skeletonLine} />
              <div className={css.skeletonLineShort} />
            </div>
          </li>
        ))}
      </ul>
    </div>
    <div className={css.summarySkeleton} />
  </div>
);

/**
 * Basket / cart page.
 *
 * Shows every item added to the client-side basket with quantity controls and
 * a running total, and hands off to CartCheckoutPage, which pays for the whole
 * basket with one card.
 */
export const CartPageComponent = props => {
  const {
    scrollingDisabled,
    isAuthenticated,
    items,
    count,
    hydrated,
    onHydrateCart,
    onUpdateQuantity,
    onRemoveFromCart,
    onRestoreCartItem,
    onClearCart,
  } = props;
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const [removed, setRemoved] = useState(null);

  // Store starts empty for SSR; hydrate the persisted basket on mount.
  useEffect(() => {
    onHydrateCart();
  }, [onHydrateCart]);

  const marketplaceName = config.marketplaceName;
  const title = intl.formatMessage({ id: 'CartPage.title' }, { marketplaceName });

  const hasItems = items.length > 0;
  const totalFormatted = formatCartSubtotal(intl, items);
  const checkoutPath = pathByRouteName('CartCheckoutPage', routeConfiguration);

  const handleRemove = item => {
    const index = items.findIndex(i => lineIdOf(i) === lineIdOf(item));
    setRemoved({ item, index });
    onRemoveFromCart(lineIdOf(item));
  };
  const handleUndo = () => {
    if (removed) {
      onRestoreCartItem(removed.item, removed.index);
      setRemoved(null);
    }
  };
  const undoBar = (
    <UndoRemoval removed={removed} onUndo={handleUndo} onDismiss={() => setRemoved(null)} />
  );

  const content = !hydrated ? (
    // Until the stored basket has been read, show its shape rather than
    // flashing "Your basket is empty" at someone whose basket is full.
    <CartSkeleton />
  ) : hasItems ? (
    <div className={css.layout}>
      <div className={css.main}>
        <Heading as="h1" rootClassName={css.pageHeading}>
          <FormattedMessage id="CartPage.headingWithCount" values={{ count }} />
        </Heading>
        {undoBar}
        <ul className={css.lineItems}>
          {items.map(item => (
            <CartLineItem
              key={lineIdOf(item)}
              item={item}
              intl={intl}
              onUpdateQuantity={onUpdateQuantity}
              onRemove={handleRemove}
            />
          ))}
        </ul>
        <InlineTextButton rootClassName={css.clearButton} onClick={onClearCart}>
          <FormattedMessage id="CartPage.clearBasket" />
        </InlineTextButton>
        <OrderEcolabelSummary
          className={css.ecolabelSummary}
          context="basket"
          products={items.map(item => ({ id: item.listingId, ecolabelKeys: item.ecolabels }))}
        />
      </div>

      <aside className={css.summary}>
        <Heading as="h2" rootClassName={css.summaryHeading}>
          <FormattedMessage id="CartPage.summaryHeading" />
        </Heading>
        <div className={css.summaryRow}>
          <span>
            <FormattedMessage id="CartPage.itemsLabel" values={{ count }} />
          </span>
          {totalFormatted ? <span>{totalFormatted}</span> : null}
        </div>
        <div className={css.summaryRow}>
          <span>
            <FormattedMessage id="CartPage.deliveryLabel" />
          </span>
          <span className={css.summaryMuted}>
            <FormattedMessage id="CartPage.deliveryAtCheckout" />
          </span>
        </div>
        <div className={css.summaryTotalRow}>
          <span>
            <FormattedMessage id="CartPage.subtotalLabel" />
          </span>
          {totalFormatted ? <span>{totalFormatted}</span> : null}
        </div>

        <CheckoutActions
          className={css.checkoutActions}
          isAuthenticated={isAuthenticated}
          checkoutPath={checkoutPath}
        />

        <p className={css.checkoutNote}>
          <FormattedMessage id="CartPage.checkoutNote" />
        </p>
      </aside>

      {/* Phones: the total and the way to checkout stay in reach while the
          shopper scrolls through a long basket. */}
      <div className={css.mobileCheckoutBar}>
        <div className={css.mobileCheckoutTotal}>
          <span className={css.mobileCheckoutLabel}>
            <FormattedMessage id="CartPage.subtotalLabel" />
          </span>
          <span className={css.mobileCheckoutValue}>{totalFormatted}</span>
        </div>
        <NamedLink
          className={css.mobileCheckoutButton}
          {...(isAuthenticated
            ? { name: 'CartCheckoutPage' }
            : {
                name: 'LoginForUserTypePage',
                params: { userType: CUSTOMER_USER_TYPE },
                to: { state: { from: checkoutPath } },
              })}
        >
          <FormattedMessage id="CartPage.checkoutButtonShort" />
        </NamedLink>
      </div>
    </div>
  ) : (
    <div className={css.root}>
      <div className={css.content}>
        {undoBar}
        <IconCart className={css.icon} />
        <Heading as="h1" rootClassName={css.heading}>
          <FormattedMessage id="CartPage.heading" />
        </Heading>
        <p className={css.emptyMessage}>
          <FormattedMessage id="CartPage.emptyMessage" />
        </p>
        <NamedLink name="SearchPage" className={css.browseButton}>
          <FormattedMessage id="CartPage.browseButton" />
        </NamedLink>
      </div>
    </div>
  );

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        {content}
      </LayoutSingleColumn>
    </Page>
  );
};

CartPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  isAuthenticated: bool,
  items: arrayOf(
    shape({
      listingId: string.isRequired,
      quantity: number.isRequired,
      title: string,
      price: object,
    })
  ).isRequired,
  count: number.isRequired,
  hydrated: bool,
  onHydrateCart: func.isRequired,
  onUpdateQuantity: func.isRequired,
  onRemoveFromCart: func.isRequired,
  onRestoreCartItem: func.isRequired,
  onClearCart: func.isRequired,
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  isAuthenticated: !!state.auth?.isAuthenticated,
  items: selectCartItems(state),
  count: selectCartCount(state),
  hydrated: selectIsCartHydrated(state),
});

const mapDispatchToProps = dispatch => ({
  onHydrateCart: () => dispatch(hydrateCart()),
  onUpdateQuantity: (lineId, quantity) => dispatch(updateCartQuantity(lineId, quantity)),
  onRemoveFromCart: lineId => dispatch(removeFromCart(lineId)),
  onRestoreCartItem: (item, index) => dispatch(restoreCartItem(item, index)),
  onClearCart: () => dispatch(clearCart()),
});

const CartPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(CartPageComponent);

export default CartPage;
